import { ChevronDown, ChevronRight, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import ErrorContent from "@/components/AI/chat/messageBlocks/ErrorBlockUI/ErrorContent";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { track } from "@/utils/events/tracker";
import {
  DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { useStartServer } from "@/utils/devServerUtils";

interface ErrorsBlockProps {
  errors: DevErrorJson[];
  isLastMessage: boolean;
}

export function ErrorsBlock({ errors, isLastMessage }: ErrorsBlockProps) {
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage,
  );
  const numFixWithAiMessages = useAiContextStore(
    (state) => state.numFixWithAiMessages,
  );

  const isChatLoading = useIsChatLoading();
  const startServer = useStartServer();

  const [isExpanded, setIsExpanded] = useState(false);

  if (!errors.length) return null;

  return (
    <div className="flex flex-col mt-[-0.2rem]! mb-1.5! w-full">
      <div className="rounded-md flex flex-col gap-1 p-2 animate-in fade-in-0 zoom-in-95 bg-secondary-n-950 border border-[#2F3035] shrink-0 w-full pl-2!">
        <div className="flex items-center justify-between w-full">
          <div
            className="flex items-center gap-1.5 cursor-pointer flex-1"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-text-secondary" />
            ) : (
              <ChevronRight className="h-4 w-4 text-text-secondary" />
            )}
            <div className="flex items-center justify-center w-5 h-5 rounded-md bg-secondary-n-900 text-secondary-n-50 text-xs font-medium">
              {errors.length}
            </div>
            <span className="text-xs text-secondary-n-50">
              {`Potential error${errors.length > 1 ? "s" : ""}`}
            </span>
          </div>
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                startServer({
                  forceRestart: true,
                });
              }}
              className="h-6 px-1 text-xs rounded-md"
              data-tooltip-id="tooltip"
              data-tooltip-content="Restart server"
              data-tooltip-place="top"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={isChatLoading || !isLastMessage}
              onClick={() => {
                useDevServerStore.getState().clearAllErrors();
              }}
              className="h-6 px-1.5 text-xs rounded-md"
              data-tooltip-id="tooltip"
              data-tooltip-content="Clear errors"
              data-tooltip-place="top"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {isExpanded && (
          <>
            <div className="border-t border-[#2F3035] -mx-2 mb-1.5 mt-1" />
            <div className="space-y-2">
              {errors.map((error, index) => (
                <ErrorContent
                  key={index}
                  message={error.message}
                  filename={error.filename}
                  lineno={error.lineno}
                  colno={error.colno}
                  stack={error.stack}
                />
              ))}
            </div>
          </>
        )}

        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={isChatLoading || !isLastMessage}
            className="flex-1 h-8 text-xs px-4 font-medium gap-1.5 rounded-md"
            style={{ border: "1px solid #2f3035" }}
            onClick={() =>
              window.open(
                "https://calendly.com/d/cntq-2xx-jzf/hire-a-human-consultation-50-deposit-required",
                "_blank",
              )
            }
          >
            Hire a Human
          </Button>
          <Button
            data-tooltip-id="tooltip"
            data-tooltip-html={
              numFixWithAiMessages >= 7
                ? "<div class='text-xs max-w-[200px]'>You've used Fix with AI 7 times in a row. Consider asking the AI for a more creative way to fix the problem.</div>"
                : ""
            }
            variant="secondary"
            size="sm"
            onClick={() => {
              const prompt = `Please make a code change to fix the above errors in my code.`;
              const sent = setSendChatMessage({
                sendChatMessage: prompt,
                isFixWithAi: true,
                includeErrorsIfAny: true,
                isFreePrompt: true,
                refreshAfterResponse: true,
              });
              if (sent) {
                track("AI_PROMPT_REQUESTED", {
                  type: "AI_CHAT_FIX_ERROR",
                  prompt,
                  isFreePrompt: true,
                });
              }
            }}
            disabled={
              isChatLoading || !isLastMessage || numFixWithAiMessages >= 7
            }
            className="w-full h-8 flex-1  text-xs gap-1.5 font-medium rounded-md bg-[#6448EF] hover:bg-[#5E44E4] text-text-primary"
          >
            Fix with AI
          </Button>
        </div>
      </div>
    </div>
  );
}
